import React, { useEffect, useRef, useState } from 'react';
import './SectionBackground.css';

/**
 * Shared section wrapper that renders a background image with scroll-triggered
 * fade-in / scale-up animation.  Reused by every data page (Buildings, Transportation, etc.).
 */

/* ── Hook: manages IntersectionObserver for a set of section IDs ────── */

export function useSectionReveal(
  sectionIds: string[],
  /** gate – observer is only created when `ready` is true (prevents firing before data loads) */
  ready: boolean = true
) {
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!ready) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-section-id');
            if (id) {
              setVisible((prev) => ({ ...prev, [id]: true }));
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );

    const timer = setTimeout(() => {
      sectionIds.forEach((id) => {
        const el = refs.current[id];
        if (el) observer.observe(el);
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, ...sectionIds]);

  return { visible, refs };
}

/* ── Component: wraps a single section with background + animation ──── */

interface SectionWrapperProps {
  sectionId: string;
  bgMap: Record<string, string>;
  refs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  visible: Record<string, boolean>;
  getSectionStyle: (id: string) => React.CSSProperties;
  children: React.ReactNode;
}

const SectionWrapper: React.FC<SectionWrapperProps> = ({
  sectionId,
  bgMap,
  refs,
  visible,
  getSectionStyle,
  children,
}) => (
  <div
    className="section-bg-wrapper"
    data-section-id={sectionId}
    ref={(el) => { refs.current[sectionId] = el; }}
    style={{
      ...getSectionStyle(sectionId),
      opacity: visible[sectionId] ? 1 : 0,
      transform: `scale(${visible[sectionId] ? 1 : 0.96})`,
    }}
  >
    {bgMap[sectionId] && (
      <img
        src={bgMap[sectionId]}
        alt=""
        aria-hidden="true"
        className="section-bg-img"
        loading="lazy"
        decoding="async"
        style={{ opacity: visible[sectionId] ? 0.18 : 0 }}
      />
    )}
    {children}
  </div>
);

export default SectionWrapper;
